import { history } from 'appHistory';
import { queryClient } from 'queryClient';
import { Routes } from 'Routes';
import IframeProvider from 'salesforceIntegrations/IframeProvider';
import { useAuth } from '@kitopi-com/auth';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { LicenseInfo } from '@mui/x-license-pro';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { authConfig } from 'auth.config';
import { AppSettingsContextProvider } from 'context/AppSettings';
import { FeatureTogglesProvider } from 'context/FeatureTogglesContext/FeatureTogglesProvider';
import type { FunctionComponent } from 'react';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { initSentry } from 'service/sentry.service';
import store, { persistor } from 'store';
import { enUsDateLocale } from 'translations/en-US';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter5Adapter } from 'use-query-params/adapters/react-router-5';
import { featureTogglesEnvironments, MUI_X_LICENSE_KEY } from 'utils/constants';

LicenseInfo.setLicenseKey(MUI_X_LICENSE_KEY);

initSentry();

const App: FunctionComponent = () => {
  useAuth(authConfig);

  return (
    <FeatureTogglesProvider environments={featureTogglesEnvironments}>
      <AppSettingsContextProvider>
        <IframeProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUsDateLocale}>
            <PersistGate loading={null} persistor={persistor}>
              <QueryClientProvider client={queryClient}>
                <Provider store={store}>
                  <Router history={history}>
                    <QueryParamProvider adapter={ReactRouter5Adapter}>
                      <Routes />
                    </QueryParamProvider>
                  </Router>
                </Provider>

                <ReactQueryDevtools buttonPosition="bottom-left" />
              </QueryClientProvider>
            </PersistGate>
          </LocalizationProvider>
        </IframeProvider>
      </AppSettingsContextProvider>
    </FeatureTogglesProvider>
  );
};

export default App;
